"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { API_BASE_URL } from "@/lib/site";

type TitleSummary = {
  slug: string;
  job_title: string;
  primary_domain_name: string | null;
};

export default function ResumeSearch({
  variant = "pill",
  popularSearches,
}: {
  variant?: "pill" | "bar";
  popularSearches?: { slug: string; label: string }[];
}) {
  const [query, setQuery] = useState("");
  const [titles, setTitles] = useState<TitleSummary[]>([]);
  const [open, setOpen] = useState(false);
  const router = useRouter();
  const containerRef = useRef<HTMLDivElement>(null);

  // The full title list is large (~8,000 titles), so it's only fetched once
  // someone actually starts searching, not on every page view.
  const requested = useRef(false);
  function loadTitles() {
    if (requested.current) return;
    requested.current = true;
    fetch(`${API_BASE_URL}/api/resumes/slugs`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setTitles(data.titles);
      })
      .catch(() => {
        requested.current = false;
      });
  }

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const results =
    query.trim().length > 0
      ? titles
          .filter((t) => t.job_title.toLowerCase().includes(query.toLowerCase()))
          .slice(0, 8)
      : [];

  function goTo(slug: string) {
    setOpen(false);
    setQuery("");
    router.push(`/resumes/${slug}`);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (results.length) goTo(results[0].slug);
  }

  const dropdown =
    open && (results.length > 0 || query.trim().length > 0) ? (
      <div className="absolute left-0 right-0 top-full z-20 mt-2 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-800 dark:bg-[#0f172a]">
        {results.length ? (
          results.map((t) => (
            <button
              key={t.slug}
              type="button"
              onClick={() => goTo(t.slug)}
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm transition hover:bg-slate-50 dark:hover:bg-white/5"
            >
              <span className="font-medium text-slate-900 dark:text-white">
                {t.job_title}
              </span>
              {t.primary_domain_name ? (
                <span className="flex-shrink-0 text-xs text-slate-400 dark:text-slate-500">
                  {t.primary_domain_name}
                </span>
              ) : null}
            </button>
          ))
        ) : (
          <div className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
            {titles.length ? <>No job titles found for &ldquo;{query}&rdquo;</> : "Loading job titles…"}
          </div>
        )}
      </div>
    ) : null;

  if (variant === "bar") {
    return (
      <div
        ref={containerRef}
        className="relative flex flex-col gap-3 rounded-[1.25rem] bg-white px-4 py-3 shadow-[0_14px_34px_-22px_rgba(15,23,42,0.35)] ring-1 ring-slate-200/70 lg:flex-row lg:items-center lg:gap-4 dark:bg-white/[0.04] dark:ring-white/10"
      >
        <form onSubmit={handleSubmit} className="flex items-center gap-2 lg:w-[360px] lg:flex-shrink-0">
          <div className="flex flex-1 items-center gap-2 rounded-full border border-slate-200 px-4 py-2 focus-within:border-slate-900 dark:border-slate-700 dark:focus-within:border-white">
            <svg
              className="flex-shrink-0 text-slate-400"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                loadTitles();
                setOpen(true);
              }}
              onFocus={() => {
                loadTitles();
                setOpen(true);
              }}
              placeholder="Search for resume examples by job title or keyword..."
              className="w-full flex-1 bg-transparent text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none dark:text-slate-200"
            />
          </div>
          <button
            type="submit"
            className="flex-shrink-0 rounded-full bg-[#0b0f19] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_10px_24px_-12px_rgba(15,23,42,0.7)] transition hover:-translate-y-0.5 dark:bg-white dark:text-[#0b0f19]"
          >
            Search
          </button>
        </form>

        {popularSearches?.length ? (
          <div className="flex min-w-0 flex-wrap items-center gap-1.5 text-xs lg:justify-end">
            <span className="flex-shrink-0 font-semibold text-slate-700 dark:text-slate-300">Popular Searches:</span>
            {popularSearches.map((p) => (
              <Link
                key={p.slug}
                href={`/resumes/${p.slug}`}
                className="rounded-full bg-white px-3 py-1 text-[11px] font-medium text-slate-700 ring-1 ring-slate-200 transition hover:ring-slate-400 dark:bg-white/5 dark:text-slate-300 dark:ring-white/10"
              >
                {p.label}
              </Link>
            ))}
          </div>
        ) : null}

        {dropdown}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative mx-auto w-full max-w-xl">
      <div className="relative">
        <svg
          className="pointer-events-none absolute left-4 top-1/2 z-10 -translate-y-1/2 text-slate-400"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.35-4.35" />
        </svg>
        <input
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            loadTitles();
            setOpen(true);
          }}
          onFocus={() => {
                loadTitles();
                setOpen(true);
              }}
          placeholder="Search job titles — e.g. Software Engineer, Nurse, Accountant…"
          className="w-full rounded-full border border-slate-200/80 bg-white/90 py-3.5 pl-12 pr-4 text-sm text-slate-700 shadow-sm backdrop-blur placeholder:text-slate-400 focus:border-slate-900 focus:outline-none dark:focus:border-white dark:border-slate-700/80 dark:bg-[#0f172a]/90 dark:text-slate-200"
        />
      </div>

      {dropdown}
    </div>
  );
}
